import React, { useState, useEffect, useRef } from "react";
import DataTable from "@/common/elements/DataTable";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import DynamicForm from "@/common/DynamicForm";
import {
  getDayjsObject,
  getFormattedDayjsObjectToString,
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import Button from "@/common/elements/Button";
import Modal from "@/common/elements/Modal";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import CommonDatePicker from "@/common/elements/DatePicker";
import { Form } from "antd";
import Select from "@/common/elements/InputElements/Select";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import TextField from "@/common/elements/InputElements/TextField";
import { v4 as uuidV4 } from "uuid";
import dayjs from "dayjs";
import useDialog from "@/hooks/useDialog";
import { setDeleteAPis } from "@/slices/bookingsSlice";
import { useDispatch, useSelector } from "react-redux";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import {
  fetchExtraTableRecord,
  saveExtraTableRecord,
} from "@/util/extraColumnActions";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";

const JailIncidentIReviewHearings = ({
  data,
  setReviewHearingData,
  isFieldEnable,
  startingPoint="",
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const dataTableRef = useRef();
  const { showDialog } = useDialog();
  const [showModal, setShowModal] = useState(false);
  const [tableData, setTableData] = useState({
    headers: [
      { key: "id", text: "ID" },
      { key: "dueDate", text: "Due Date" },
      { key: "dueTime", text: "Due Time" },
      { key: "actualDate", text: "Actual Date" },
      { key: "actualTime", text: "Actual Time" },
      { key: "supervisor", text: "Supervisor" },
      { key: "jailIncidentsResults", text: "JaillncidentsResults" },
      { key: "appeal", text: "Appeal" },
      { key: "comments", text: "Comments" },
    ],
    rows: [],
  });
  const [selectedReviewHearingData, setSelectedReviewHearingData] =
    useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isModify, setisModify] = useState(false);

  // -----------------------------------------------------------------------
  // JailIncidentsInvolvedInmateReviewHearingsExtra — PCI's per-client
  // dynamic "_U" columns for one review hearing entry
  // (JailIncidentsInvolvedInmateReviewHearingsInterface.asp's "Additional
  // Review Hearing Information" section, right after Comments).
  // -----------------------------------------------------------------------
  const [reviewHearingExtraFields, setReviewHearingExtraFields] = useState([]);
  const [reviewHearingExtraFieldsReady, setReviewHearingExtraFieldsReady] = useState(false);
  const reviewHearingExtraTypeMapRef = useRef({});
  const reviewHearingExtraIdRef = useRef(0);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/JailIncidentsInvolvedInmateReviewHearingsExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
        const converted = convertApiFields(tableData, "JailIncidentsInvolvedInmateReviewHearingsExtra");
        const typeMap = {};
        converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
        reviewHearingExtraTypeMapRef.current = typeMap;
        setReviewHearingExtraFields(converted);
        setReviewHearingExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error("[JailIncidentIReviewHearings] JailIncidentsInvolvedInmateReviewHearingsExtra field defs fetch failed:", error);
      });
  }, []);

  // Fetches the saved extra row once a real id is present (edit/view mode)
  // — a brand new review hearing has no id yet, so there's nothing to fetch
  // until it's saved once.
  useEffect(() => {
    if (!reviewHearingExtraFieldsReady) return;
    const id = selectedReviewHearingData?.jailIncidentsInvolvedInmateReviewHearingId;
    if (!isNonEmptyVariable(id)) {
      reviewHearingExtraIdRef.current = 0;
      return;
    }

    fetchExtraTableRecord(
      "JailIncidentsInvolvedInmateReviewHearingsExtra",
      "jailIncidentsInvolvedInmateReviewHearingID",
      id,
    )
      .then((result) => {
        if (!result) return;
        reviewHearingExtraIdRef.current =
          result.raw?.JailIncidentsInvolvedInmateReviewHearingsExtraID ?? 0;
        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = reviewHearingExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        form.setFieldsValue(withDates);
      })
      .catch((error) => {
        console.error("[JailIncidentIReviewHearings] JailIncidentsInvolvedInmateReviewHearingsExtra fetch failed:", error);
      });
  }, [selectedReviewHearingData, reviewHearingExtraFieldsReady]);

  useEffect(() => {
    if (isNonEmptyVariable(data) && data?.length) {
      const rows = data?.map((row, index) => ({
        uuid: uuidV4(),
        id: row?.jailIncidentsInvolvedInmateReviewHearingId || "",
        dueDate: row?.dueDate,
        dueTime: row?.dueTime,
        actualDate: row?.actualDate,
        actualTime: row?.actualTime,
        supervisor: getRefListNameByID("", row?.supervisorStaffId, true, true),
        supervisorStaffId: row?.supervisorStaffId,
        jailIncidentsResults: row?.jailIncidentsResults,
        appeal: row?.appeal ? "Yes" : "No",
        comments: row?.comments,
      }));
      setTableData((prev) => ({ ...prev, rows }));
    } else {
      setTableData((prev) => ({ ...prev, rows: [] }));
    }
  }, [data]);

  const handleAction = (action) => {
    switch (action) {
      case "add":
        setisModify(true);
        setEditingIndex(null);
        setShowModal(true);
        break;
      case "edit":
        setisModify(true);
        setShowModal(true);

        form.setFieldsValue({
          ...selectedReviewHearingData,
          dueDateTime: getDayjsObject(
            selectedReviewHearingData?.dueDate,
            selectedReviewHearingData?.dueTime,
          ),
          actualDateTime: getDayjsObject(
            selectedReviewHearingData?.actualDate,
            selectedReviewHearingData?.actualTime,
          ),
        });
        break;
      case "view":
        setisModify(false);
        setShowModal(true);
        form.setFieldsValue({
          ...selectedReviewHearingData,
          dueDateTime: getDayjsObject(
            selectedReviewHearingData?.dueDate,
            selectedReviewHearingData?.dueTime,
          ),
          actualDateTime: getDayjsObject(
            selectedReviewHearingData?.actualDate,
            selectedReviewHearingData?.actualTime,
          ),
        });
        break;
      case "delete":
        showDialog("confirm", {
          title: "Delete Action",
          content: `When you save this record, the object will be deleted. 
If you change your mind about the deleting the object, 
click Cancel in the Action Pane and the object will not be deleted.
`,
          onConfirm: () => deleteReviewHearing(),
          oncancel: () => onCloseModal(),
        });
        break;
      default:
        return;
    }
  };

  const handleRowClick = (value, index) => {
    console.log("Indexxx : ",index);
    console.log("Value", value);

    if(index !== undefined && index !== null) {
       if (!isNonEmptyVariable(value?.uuid)) {
        return;
      }

      setSelectedReviewHearingData(value);
      setEditingIndex(index);
      if(isFieldEnable) {
        handleAction("edit");
      } else {
        handleAction("view");
      }
    }
    else {
      if (!isNonEmptyVariable(value?.uuid)) {
        return;
      }

      setSelectedReviewHearingData(value);
      setEditingIndex(index);
    }
  };

  const handleSubmit = () => {
    form?.validateFields().then((values) => {
      // Split JailIncidentsInvolvedInmateReviewHearingsExtra's dynamic
      // fields out of the form values — they're saved separately via the
      // generic extra-table endpoint, not folded into the deferred
      // reviewHearingData rows.
      const reviewHearingExtraFieldKeys = reviewHearingExtraFields.map(
        (f) => f.columnName,
      );
      const reviewHearingExtraSnapshot = {};
      reviewHearingExtraFieldKeys.forEach((k) => {
        if (k in values) {
          const val = values[k];
          const htmlType = reviewHearingExtraTypeMapRef.current[k];
          reviewHearingExtraSnapshot[k] = dayjs.isDayjs(val)
            ? val.format(
                htmlType === "datetime" ? "MM/DD/YYYY HH:mm" : "MM/DD/YYYY",
              )
            : val;
          delete values[k];
        }
      });

      // Only an existing review hearing (a real
      // jailIncidentsInvolvedInmateReviewHearingId) can be saved
      // immediately — a brand new one only gets that id once the whole
      // Jail Incident record is saved, which this modal doesn't wait for.
      const existingReviewHearingId =
        selectedReviewHearingData?.jailIncidentsInvolvedInmateReviewHearingId;
      if (
        reviewHearingExtraFieldKeys.length > 0 &&
        isNonEmptyVariable(existingReviewHearingId)
      ) {
        saveExtraTableRecord({
          tableName: "JailIncidentsInvolvedInmateReviewHearingsExtra",
          idColumn: "jailIncidentsInvolvedInmateReviewHearingID",
          idValue: existingReviewHearingId,
          extraRecord: {
            ...reviewHearingExtraSnapshot,
            jailIncidentsInvolvedInmateReviewHearingsExtraID:
              reviewHearingExtraIdRef.current || undefined,
          },
          extraIdKey: "jailIncidentsInvolvedInmateReviewHearingsExtraID",
        }).catch((error) => {
          console.error(
            "[JailIncidentIReviewHearings] JailIncidentsInvolvedInmateReviewHearingsExtra save failed:",
            error,
          );
        });
      }

      const _dueDateTime = getFormattedDayjsObjectToString(values?.dueDateTime);
      const _actualDateTime = getFormattedDayjsObjectToString(
        values?.actualDateTime,
      );
      const rows = tableData?.rows?.length ? tableData?.rows : [];

      const modifieValues = {
        ...values,
        dueDate: isNonEmptyVariable(_dueDateTime)
          ? _dueDateTime?.split(" ")?.[0]
          : null,
        dueTime: isNonEmptyVariable(_dueDateTime)
          ? _dueDateTime?.split(" ")?.[1]
          : null,
        actualDate: isNonEmptyVariable(_actualDateTime)
          ? _actualDateTime?.split(" ")?.[0]
          : null,
        actualTime: isNonEmptyVariable(_actualDateTime)
          ? _actualDateTime?.split(" ")?.[1]
          : null,
      };
      if (editingIndex !== null) {
        rows[editingIndex] = {
          ...rows?.[editingIndex],
          ...modifieValues,
        };
      } else {
        rows.push({
          ...modifieValues,
        });
      }

      setReviewHearingData?.(() => rows);
      onCloseModal();
    });
  };

  const onCloseModal = () => {
    dataTableRef?.current?.resetAll();
    form.resetFields();
    setisModify(false);
    setSelectedReviewHearingData(null);
    setEditingIndex(null);
    setShowModal(false);
  };

  const deleteReviewHearing = () => {
    const _reviewHearingData = data?.filter(
      (_, index) => index !== editingIndex,
    );

    setReviewHearingData(() => _reviewHearingData);

    if (isNonEmptyVariable(selectedReviewHearingData?.id)) {
      const url =
        API_ENDPOINTS.jailIncidents.deleteReviewHearing +
        selectedReviewHearingData?.id;
      dispatch(setDeleteAPis(url));
    }

    onCloseModal();
  };

  return (
    <>
      <StyledSectionHeading marginTop="20px">
        <span>Review Hearings</span>
      </StyledSectionHeading>

      <StyledSubTableContainer>
        <StyledFlex align="center" justify="space-between">
          <h2>List of Jail Incidents Review Hearings</h2>

          <StyledFlex marginBottom="10px" gap="10px">
            <Button
              onClick={() => handleAction("add")}
              disabled={!isFieldEnable}
            >
              Add
            </Button>
            <Button
              onClick={() => handleAction("view")}
              disabled={selectedReviewHearingData === null}
            >
              Details
            </Button>
            <Button
              onClick={() => handleAction("edit")}
              disabled={selectedReviewHearingData === null || !isFieldEnable}
            >
              Modify
            </Button>
            <Button
              onClick={() => handleAction("delete")}
              disabled={selectedReviewHearingData === null || !isFieldEnable}
            >
              Delete
            </Button>
          </StyledFlex>
        </StyledFlex>

        <DataTable
          ref={dataTableRef}
          headers={tableData.headers}
          data={tableData.rows || []}
          rowUniqueId="uuid"
          onRowClick={handleRowClick}
          selected={selectedReviewHearingData === null}
        />
      </StyledSubTableContainer>

      {showModal && (
        <Modal
          show={showModal}
          header="Jail Incident Review Hearing"
          handleCancel={onCloseModal}
          handleSubmit={handleSubmit}
          hideSubmitButton={!isModify || !isFieldEnable}
          onHide={onCloseModal}
        >
          <FieldSecurityProvider
            entity={SCHEMA_NAME.JAIL_INCIDENTS_INVOLVED_INMATE}
            startingPoint={startingPoint}
            bookingsFieldsEnabled={isFieldEnable}
          >
            <Form form={form}>
              <StyledGridRow gutter={16}>
                <StyledGridCol lg={8}>
                  <SecureFormItem
                    fieldName="dueDateTime"
                    formItemProps={{ 
                      label: "Due Date/Time",
                      name: "dueDateTime",
                    }}
                  >
                    <CommonDatePicker
                      showTime={true}
                      format="MM/DD/YYYY HH:mm"
                      disabled={!isModify || !isFieldEnable}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={8}>
                  <SecureFormItem
                    fieldName="actualDateTime"
                    formItemProps={{ 
                      label: "Actual Date/Time",
                      name: "actualDateTime",
                    }}
                  >
                    <CommonDatePicker
                      showTime={true}
                      format="MM/DD/YYYY HH:mm"
                      disabled={!isModify || !isFieldEnable}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={8}>
                  <SecureFormItem
                    fieldName="supervisorStaffId"
                    formItemProps={{ 
                      label: "Supervisor",
                      name: "supervisorStaffId",
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList("", true, false, true)}
                      disabled={!isModify || !isFieldEnable}
                    />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>

              <StyledGridRow gutter={16}>
                <StyledGridCol lg={8}>
                  <SecureFormItem
                    fieldName="jailIncidentsResults"
                    formItemProps={{ 
                      label: "Result",
                      name: "jailIncidentsResults",
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList("JailIncidentsResults")}
                      disabled={!isModify || !isFieldEnable}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={8}>
                  <SecureFormItem
                    fieldName="appeal"
                    formItemProps={{ 
                      label: "Appeal",
                      name: "appeal",
                      layout: "horizontal",
                      labelPostion: "label-right",
                      paddingTop: "26px",
                      valuePropName: "checked",
                    }}
                  >
                    <Checkbox disabled={!isModify || !isFieldEnable} />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>

              <StyledGridRow gutter={16}>
                <StyledGridCol lg={24}>
                  <SecureFormItem
                    fieldName="comments"
                    formItemProps={{
                      label: "Comments",
                      name: "comments",
                    }}
                  >
                    <TextField
                      inputType="textarea"
                      rows={4}
                      disabled={!isModify || !isFieldEnable}
                    />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>

              {reviewHearingExtraFields.length > 0 && (
                <FieldSecurityProvider
                  entity="JailIncidentsInvolvedInmateReviewHearingsExtra"
                  startingPoint={startingPoint}
                  bookingsFieldsEnabled={isModify && isFieldEnable}
                >
                  <StyledSectionHeading marginTop="20px">
                    <span>Additional Review Hearing Information</span>
                  </StyledSectionHeading>
                  <StyledGridRow gutter={16}>
                    {reviewHearingExtraFields.map((sf, index) => (
                      <DynamicForm
                        key={index}
                        formAttributes={sf}
                        form={form}
                        bookingsFieldsEnabled={isModify && isFieldEnable}
                      />
                    ))}
                  </StyledGridRow>
                </FieldSecurityProvider>
              )}
            </Form>
          </FieldSecurityProvider>
        </Modal>
      )}
    </>
  );
};

export default JailIncidentIReviewHearings;
